import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, formatDistanceToNow, parseISO } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * True when a contact's email is flagged clearly undeliverable — email_status
 * 'invalid' or the 'invalid email' tag (legacy: 'fake_email') (bug 21670).
 * Mirrors the backend is_undeliverable_contact() guard. Such contacts must not
 * be enriched, re-enriched, or researched (it would burn credits on a dead
 * address).
 */
export const INVALID_EMAIL_TAG = 'invalid email'
// Tag values (lowercased) that mark an email undeliverable — new + legacy.
const INVALID_EMAIL_TAGS = new Set([INVALID_EMAIL_TAG, 'fake_email'])

export function isUndeliverableContact(
  contact: { email_status?: string | null; tags?: (string | null)[] | null } | null | undefined
): boolean {
  if (!contact) return false
  if ((contact.email_status || '').toLowerCase() === 'invalid') return true
  return (contact.tags || []).some(t => INVALID_EMAIL_TAGS.has((t || '').toLowerCase()))
}

export function formatDate(date: string | Date, formatStr: string = "PPP") {
  if (typeof date === "string") {
    return format(parseISO(date), formatStr)
  }
  return format(date, formatStr)
}

export function formatDateTime(date: string | Date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(date))
}

export function formatRelativeTime(date: string | Date) {
  if (typeof date === "string") {
    return formatDistanceToNow(parseISO(date), { addSuffix: true })
  }
  return formatDistanceToNow(date, { addSuffix: true })
}

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func.apply(null, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

export function capitalizeFirst(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export function isValidEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function getInitials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function truncate(str: string, length: number) {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function isValidUrl(url: string) {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function formatNumber(num: number) {
  return new Intl.NumberFormat().format(num)
}

export function formatCurrency(amount: number, currency: string = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

export function formatPercentage(value: number, decimals: number = 1) {
  return `${(value * 100).toFixed(decimals)}%`
}

export function removeDuplicates<T>(array: T[], key?: keyof T): T[] {
  if (!key) {
    return [...new Set(array)]
  }
  const seen = new Set()
  return array.filter((item) => {
    const value = item[key]
    if (seen.has(value)) {
      return false
    }
    seen.add(value)
    return true
  })
}

export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((groups, item) => {
    const value = String(item[key])
    groups[value] = groups[value] || []
    groups[value].push(item)
    return groups
  }, {} as Record<string, T[]>)
}

export function getStoredValue<T>(key: string, defaultValue: T): T {
  try {
    const item = window.localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch {
    return defaultValue
  }
}

export function setStoredValue<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Silently fail
  }
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

// Shared badge variant styles
export const badgeVariantStyles = {
  default: 'bg-muted text-muted-foreground',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400',
  destructive: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400',
  info: 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]',
} as const

export type BadgeVariant = keyof typeof badgeVariantStyles

/**
 * Bordered variant of the same four tones, for pills that sit on a card surface
 * and need an outline to separate from it (workflow/campaign selection rows).
 * Same hues as `badgeVariantStyles` — never a fifth tone (§31).
 */
export const badgeVariantBorderStyles: Record<BadgeVariant, string> = {
  default: 'bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]',
  success: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:bg-emerald-500/[0.10] dark:text-emerald-300 dark:border-emerald-500/20',
  warning: 'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:bg-amber-500/[0.10] dark:text-amber-300 dark:border-amber-500/20',
  destructive: 'bg-red-500/10 text-red-700 border-red-500/20 dark:bg-red-500/[0.10] dark:text-red-300 dark:border-red-500/20',
  info: 'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/25',
} as const

/**
 * Icon-chip tint per tone — the square chip that carries a status glyph
 * (leading chip on a list row, stat pill icon). Split bg/fg so the chip and the
 * glyph can be styled independently.
 */
export const badgeVariantIconTint: Record<BadgeVariant, { bg: string; fg: string }> = {
  default: { bg: 'bg-muted/60 dark:bg-white/[0.04]', fg: 'text-muted-foreground dark:text-[#8a8f98]' },
  success: { bg: 'bg-emerald-500/10 dark:bg-emerald-500/[0.10]', fg: 'text-emerald-600 dark:text-emerald-400' },
  warning: { bg: 'bg-amber-500/10 dark:bg-amber-500/[0.10]', fg: 'text-amber-600 dark:text-amber-400' },
  destructive: { bg: 'bg-red-500/10 dark:bg-red-500/[0.12]', fg: 'text-red-600 dark:text-red-400' },
  info: { bg: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12]', fg: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
} as const

/**
 * Status → tone. The ONE place a state's colour is decided; every chip, badge,
 * dot, icon tint and pill in the product reads it through the getters below.
 *
 * Lifecycle ruling (§60): in-flight is brand indigo (`info`), finished-well is
 * emerald (`success`), held/queued is amber (`warning`), terminal-bad is red
 * (`destructive`), inert is muted (`default`). That is why `running` is `info`
 * and not `success` — running is not an outcome — and why `completed` is
 * `success` at both campaign and stage level instead of drifting between
 * muted, indigo and emerald across three surfaces.
 */
export const statusToBadge: Record<string, BadgeVariant> = {
  running: 'info',
  active: 'success',
  verified: 'success',
  qualified: 'success',
  engaged: 'success',
  completed: 'success',
  sent: 'default',
  new: 'default',
  contacted: 'warning',
  scheduled: 'warning',
  paused: 'warning',
  pending: 'warning',
  // Awaiting an approver — "held, queued", the same tone as scheduled/paused.
  pending_approval: 'warning',
  verifying: 'warning',
  draft: 'default',
  // A declined approval request — terminal-bad, same tone as failed/stopped.
  rejected: 'destructive',
  failed: 'destructive',
  stopped: 'destructive',
  bounced: 'destructive',
  invalid: 'destructive',
  fake_email: 'destructive',       // legacy tag value
  'invalid email': 'destructive',
  unsubscribed: 'warning',
  unqualified: 'destructive',
  complained: 'destructive',
}

export function getBadgeClasses(status: string = ''): string {
  const variant = statusToBadge[status] || 'default'
  return badgeVariantStyles[variant]
}

export const badgeDotColors: Record<BadgeVariant, string> = {
  default: 'bg-muted-foreground',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  destructive: 'bg-red-500',
  info: 'bg-[#5e6ad2] dark:bg-[#a5b4fc]',
}

export function getBadgeDotColor(status: string = ''): string {
  const variant = statusToBadge[status] || 'default'
  return badgeDotColors[variant]
}

/** Tone of a status, for callers that need the variant itself (icon, ring, glow). */
export function getBadgeVariant(status: string = ''): BadgeVariant {
  return statusToBadge[status] || 'default'
}

/** Bordered chip classes for a status — same tone as `getBadgeClasses`. */
export function getBadgeBorderClasses(status: string = ''): string {
  return badgeVariantBorderStyles[getBadgeVariant(status)]
}

/** Icon-chip tint (bg + glyph colour) for a status. */
export function getBadgeIconTint(status: string = ''): { bg: string; fg: string } {
  return badgeVariantIconTint[getBadgeVariant(status)]
}

/**
 * Convert plain-text email body to formatted HTML.
 * Mirrors the backend's _text_to_html(): double newlines become paragraphs,
 * single newlines become <br>, and HTML entities are escaped.
 */
export function emailBodyToHtml(text: string): string {
  if (!text) return ''
  // Escape HTML entities
  let safe = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  // Split on double newlines → paragraphs
  const paragraphs = safe.split(/\n\s*\n/)
  return paragraphs
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('')
}